import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import Header from "../components/Header";
import "../styles/global.css";
import "../styles/result.css";
import { Helmet } from "react-helmet";
const Result = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { result } = location.state || {};
  const [activeTipIndex, setActiveTipIndex] = useState(null);
  const [isMessageExpanded, setIsMessageExpanded] = useState(true);
  const [isExplanationExpanded, setIsExplanationExpanded] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showResourcesSection, setShowResourcesSection] = useState(false);
  //const [emailInput, setEmailInput] = useState("");
  const resultHeaderRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    // Initialize ads
    try {
      if (window.adsbygoogle && process.env.NODE_ENV !== "development") {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      }
    } catch (e) {
      console.error("AdSense error:", e);
    }

    // Delayed setting of isLoaded to trigger animations
    setTimeout(() => {
      setIsLoaded(true);

      // If positive result, trigger confetti after a delay
      if (result && result.recommendation === "Stay") {
        setTimeout(launchConfetti, 1000);
      }
    }, 300);

    // Show resources section after delay
    setTimeout(() => {
      setShowResourcesSection(true);
    }, 2000);
  }, [result]);

  const launchConfetti = () => {
    if (resultHeaderRef.current) {
      const rect = resultHeaderRef.current.getBoundingClientRect();
      const x = (rect.left + rect.right) / 2 / window.innerWidth;
      const y = (rect.top + rect.bottom) / 2 / window.innerHeight;

      confetti({
        particleCount: 100,
        spread: 70,
        origin: { x, y: y - 0.1 },
        colors: ["#00d4a1", "#6a1cb1", "#8a2be2"],
        disableForReducedMotion: true,
      });
    }
  };

  const handleTipPress = (index) => {
    setActiveTipIndex(activeTipIndex === index ? null : index);
  };

  const toggleMessage = () => {
    setIsMessageExpanded(!isMessageExpanded);
  };

  const toggleExplanation = () => {
    setIsExplanationExpanded(!isExplanationExpanded);
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: "My Relationship Assessment Results",
          text: `My relationship assessment shows that I should ${result.recommendation}. Check out this tool!`,
          url: window.location.origin,
        })
        .catch((error) => console.log("Error sharing:", error));
    } else {
      // Fallback for browsers that don't support sharing
      alert("Share this page via:\n" + window.location.href);
    }
  };

  // const handleEmailSubmit = (e) => {
  //   e.preventDefault();
  //   if (emailInput && emailInput.includes("@")) {
  //     alert(`Thank you! Your detailed results will be sent to ${emailInput}`);
  //     setEmailInput("");
  //   } else {
  //     alert("Please enter a valid email address");
  //   }
  // };

  const resources = {
    Stay: [
      {
        title: "Communication Techniques",
        description: "Learn effective ways to strengthen your connection",
        icon: "fa-comments",
        link: "/resources/communication",
      },
      {
        title: "Building Trust",
        description: "Practical steps to deepen trust in your relationship",
        icon: "fa-handshake",
        link: "/resources/trust",
      },
      {
        title: "Conflict Resolution",
        description: "Healthy approaches to disagreements",
        icon: "fa-balance-scale",
        link: "/resources/conflict",
      },
    ],
    Leave: [
      {
        title: "Self-Care Practices",
        description: "Essential ways to nurture yourself during transition",
        icon: "fa-heart",
        link: "/resources/self-care",
      },
      {
        title: "Building Support Networks",
        description: "Creating connections that help you thrive",
        icon: "fa-users",
        link: "/resources/support",
      },
      {
        title: "Setting Boundaries",
        description: "Establishing healthy limits in relationships",
        icon: "fa-shield-alt",
        link: "/resources/boundaries",
      },
    ],
  };

  if (!result) {
    return (
      <div className="result-empty-state">
        <Header />
        <Helmet>
          <title>Result: Ready to view our decision</title>
          <meta
            name="description"
            content="Result page that has a decision made by our AI to help you decide about you relationship status"
          />
        </Helmet>
        <div className="result-empty-content">
          <motion.div
            className="result-empty-graphic"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}>
            <i className="fas fa-clipboard-question"></i>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}>
            No Results Available
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}>
            Please complete the relationship assessment to receive your
            personalized insights
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}>
            <Link to="/quiz" className="result-primary-button">
              Start Assessment
              <i className="fas fa-arrow-right"></i>
            </Link>
          </motion.div>

          {/* Empty state ad placement */}
          <div
            className="ad-container empty-state-ad"
            style={{ width: "100%", minWidth: "300px" }}>
            {/* <div className="ad-label">Advertisement</div> */}
            <ins
              className="adsbygoogle"
              style={{ display: "block" }}
              data-ad-client="ca-pub-2067522743161930"
              data-ad-slot="1234567890"
              data-ad-format="auto"
              data-full-width-responsive="true"></ins>
          </div>
        </div>
      </div>
    );
  }

  const isPositive = result.recommendation === "Stay";
  const recommendationResources =
    resources[result.recommendation] || resources["Leave"];

  return (
    <div
      className={`result-container ${
        isPositive ? "positive-result" : "negative-result"
      }`}>
      <div className="result-background-decoration">
        <div className="result-shape shape1"></div>
        <div className="result-shape shape2"></div>
        <div className="result-shape shape3"></div>
      </div>

      <Header />

      <main className="result-content-wrapper">
        <motion.section
          className="result-hero-section"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8 }}
          ref={resultHeaderRef}>
          <div className="result-hero-content">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}>
              Your Relationship Assessment
            </motion.h1>

            <motion.div
              className="result-headline"
              initial={{ opacity: 0, y: 30 }}
              animate={isLoaded ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.4 }}>
              <div
                className={`result-recommendation-tag ${
                  isPositive ? "positive" : "negative"
                }`}>
                <span className="recommendation-text">
                  {result.recommendation}
                </span>
                <span className="recommendation-icon">
                  <i
                    className={`fas ${
                      isPositive ? "fa-heart" : "fa-heart-broken"
                    }`}></i>
                </span>
              </div>
            </motion.div>

            <motion.p
              className="result-subtitle"
              initial={{ opacity: 0 }}
              animate={isLoaded ? { opacity: 1 } : {}}
              transition={{ duration: 0.7, delay: 0.5 }}>
              {isPositive
                ? "Your answers indicate this relationship has strong potential. Focus on the areas below to nurture your connection."
                : "Your responses indicate some significant concerns. Consider the insights below as you determine your next steps."}
            </motion.p>
          </div>
        </motion.section>

        <div className="result-analysis-wrapper">
          {/* Top Ad Placement */}
          <div
            className="ad-container top-result-ad"
            style={{ width: "100%", minWidth: "300px" }}>
            {/* <div className="ad-label">Advertisement</div> */}
            <ins
              className="adsbygoogle"
              style={{ display: "block" }}
              data-ad-client="ca-pub-2067522743161930"
              data-ad-slot="1234567891"
              data-ad-format="auto"
              data-full-width-responsive="true"></ins>
          </div>

          <div className="result-analysis-container">
            <div className="result-main-column">
              {!isPositive && (
                <motion.div
                  className="result-card message-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.5, delay: 0.6 }}>
                  <div className="result-card-header" onClick={toggleMessage}>
                    <div className="result-card-header-content">
                      <div className="result-card-icon">
                        <i className="fas fa-comment-dots"></i>
                      </div>
                      <h3>A Personal Note For You</h3>
                    </div>
                    <button
                      className="result-expand-button"
                      aria-label="Toggle content">
                      <i
                        className={`fas fa-chevron-${
                          isMessageExpanded ? "up" : "down"
                        }`}></i>
                    </button>
                  </div>

                  <AnimatePresence initial={false}>
                    {isMessageExpanded && (
                      <motion.div
                        className="result-card-body"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}>
                        <p>
                          We understand this may be difficult to hear. Making
                          decisions about relationships is never easy,
                          especially when you've invested time, energy, and
                          emotion.
                        </p>
                        <p>
                          Remember that prioritizing your wellbeing isn't
                          selfish—it's necessary. You deserve relationships that
                          help you flourish, not just survive. This assessment
                          reflects patterns based on your responses, but you are
                          the ultimate expert on your life.
                        </p>
                        <p>
                          Whatever you decide, take it one step at a time and be
                          gentle with yourself during this process.
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              )}

              <motion.div
                className="result-card explanation-card"
                initial={{ opacity: 0, y: 20 }}
                animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.7 }}>
                <div className="result-card-header" onClick={toggleExplanation}>
                  <div className="result-card-header-content">
                    <div className="result-card-icon">
                      <i className="fas fa-lightbulb"></i>
                    </div>
                    <h3>Key Insights</h3>
                  </div>
                  <button
                    className="result-expand-button"
                    aria-label="Toggle content">
                    <i
                      className={`fas fa-chevron-${
                        isExplanationExpanded ? "up" : "down"
                      }`}></i>
                  </button>
                </div>

                <AnimatePresence initial={false}>
                  {isExplanationExpanded && (
                    <motion.div
                      className="result-card-body"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}>
                      <p>{result.explanation}</p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>

              {/* Mid-page Ad - After explanation */}
              <div
                className="ad-container mid-result-ad"
                style={{ width: "100%", minWidth: "300px" }}>
                {/* <div className="ad-label">Advertisement</div> */}
                <ins
                  className="adsbygoogle"
                  style={{ display: "block" }}
                  data-ad-client="ca-pub-2067522743161930"
                  data-ad-slot="1234567892"
                  data-ad-format="auto"
                  data-full-width-responsive="true"></ins>
              </div>

              <motion.section
                className="result-action-steps"
                initial={{ opacity: 0, y: 20 }}
                animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.8 }}>
                <h2 className="result-section-title">
                  <span>Recommended Action Steps</span>
                </h2>

                <div className="result-steps-list">
                  {result.tips.map((tip, index) => {
                    const [title, content] = tip.includes(":")
                      ? [
                          tip.split(":")[0],
                          tip.split(":").slice(1).join(":").trim(),
                        ]
                      : [tip, ""];

                    return (
                      <motion.div
                        key={index}
                        className="result-card step-card"
                        initial={{ opacity: 0, y: 20 }}
                        animate={isLoaded ? { opacity: 1, y: 0 } : {}}
                        transition={{
                          duration: 0.3,
                          delay: 0.8 + index * 0.1,
                        }}>
                        <div
                          className="result-card-header"
                          onClick={() => handleTipPress(index)}>
                          <div className="result-card-header-content">
                            <div className="result-step-number">
                              {index + 1}
                            </div>
                            <h3>{title}</h3>
                          </div>
                          <button
                            className="result-expand-button"
                            aria-label="Toggle content">
                            <i
                              className={`fas fa-chevron-${
                                activeTipIndex === index ? "up" : "down"
                              }`}></i>
                          </button>
                        </div>

                        <AnimatePresence initial={false}>
                          {activeTipIndex === index && (
                            <motion.div
                              className="result-card-body"
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.3 }}>
                              <p>
                                {content ||
                                  "Take time to reflect on this step and consider how you might implement it in your situation."}
                              </p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.section>

              <AnimatePresence>
                {showResourcesSection && (
                  <motion.section
                    className="result-resources-section"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}>
                    <h2 className="result-section-title">
                      <span>Helpful Resources</span>
                    </h2>

                    <div className="result-resources-grid">
                      {recommendationResources.map((resource, index) => (
                        <Link
                          key={index}
                          to={resource.link}
                          className="result-resource-card">
                          <div className="resource-icon">
                            <i className={`fas ${resource.icon}`}></i>
                          </div>
                          <div className="resource-content">
                            <h3>{resource.title}</h3>
                            <p>{resource.description}</p>
                          </div>
                          <div className="resource-arrow">
                            <i className="fas fa-arrow-right"></i>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </motion.section>
                )}
              </AnimatePresence>
            </div>

            <div className="result-sidebar">
              <div className="result-sticky-sidebar">
                {/* <div className="result-sidebar-card get-pdf">
                  <h3>Save Your Results</h3>
                  <p>Enter your email to receive a detailed PDF analysis of your relationship assessment.</p>
                  
                  <form className="result-email-form" onSubmit={handleEmailSubmit}>
                  <input 
                      type="email" 
                      placeholder="Your email address" 
                      value={emailInput} 
                      onChange={(e) => setEmailInput(e.target.value)}
                      required
                    />
                    <button type="submit">
                      <i className="fas fa-envelope"></i>
                      <span>Get PDF</span>
                    </button>
                  </form>
                </div> */}

                {/* Sidebar Ad Placement */}
                <div
                  className="ad-container sidebar-result-ad"
                  style={{ width: "100%", minWidth: "300px" }}>
                  {/* <div className="ad-label">Advertisement</div> */}
                  <ins
                    className="adsbygoogle"
                    style={{ display: "block" }}
                    data-ad-client="ca-pub-2067522743161930"
                    data-ad-slot="1234567893"
                    data-ad-format="auto"
                    data-full-width-responsive="true"></ins>
                </div>

                <div className="result-sidebar-card next-steps">
                  <h3>What's Next?</h3>

                  <div className="result-action-buttons">
                    <button
                      className="result-primary-button"
                      onClick={() => navigate("/quiz")}>
                      <i className="fas fa-redo"></i>
                      <span>Retake Assessment</span>
                    </button>

                    <button
                      className="result-secondary-button"
                      onClick={handleShare}>
                      <i className="fas fa-share-alt"></i>
                      <span>Share Results</span>
                    </button>
                  </div>
                </div>

                {isPositive ? (
                  <div className="result-sidebar-card testimonial">
                    <div className="testimonial-quote">
                      <i className="fas fa-quote-left"></i>
                      <p>
                        This assessment helped us identify our strengths and
                        areas for growth. Six months later, our relationship is
                        stronger than ever.
                      </p>
                      <div className="testimonial-author">
                        <img
                          src="assets/images/couple-testimonial.jpeg"
                          alt="Jessica & Mark"
                        />
                        <span>Jessica & Mark</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="result-sidebar-card inspiration">
                    <div className="inspiration-quote">
                      <i className="fas fa-quote-left"></i>
                      <p>
                        Sometimes the bravest and most important thing you can
                        do is just show up and tell your story.
                      </p>
                      <div className="quote-author">— Brené Brown</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Ad Placement */}
          <div
            className="ad-container bottom-result-ad"
            style={{ width: "100%", minWidth: "300px" }}>
            {/* <div className="ad-label">Advertisement</div> */}
            <ins
              className="adsbygoogle"
              style={{ display: "block" }}
              data-ad-client="ca-pub-2067522743161930"
              data-ad-slot="1234567894"
              data-ad-format="auto"
              data-full-width-responsive="true"></ins>
          </div>
        </div>

        <section className="result-related-content">
          <div className="result-related-content-wrapper">
            <h2>Continue Your Journey</h2>

            <div className="result-related-articles">
              <div className="result-article-card">
                <div className="article-image">
                  <img
                    src={`assets/images/${
                      isPositive
                        ? "communication-tips.jpeg"
                        : "self-care-guide.jpeg"
                    }`}
                    alt="Article thumbnail"
                  />
                </div>
                <div className="article-content">
                  <h3>
                    {isPositive
                      ? "Communication Tips For Deeper Connection"
                      : "Self-Care Guide For Relationship Transitions"}
                  </h3>
                  <p>
                    {isPositive
                      ? "Learn practical strategies to improve understanding and intimacy with your partner."
                      : "Essential practices to support your wellbeing during relationship changes."}
                  </p>
                  <Link
                    to={`/blog/${
                      isPositive ? "communication-tips" : "self-care-guide"
                    }`}
                    className="article-link">
                    Read Article
                    <i className="fas fa-arrow-right"></i>
                  </Link>
                </div>
              </div>

              <div className="result-article-card">
                <div className="article-image">
                  <img
                    src={`assets/images/${
                      isPositive
                        ? "relationship-goals.jpeg"
                        : "moving-forward.jpeg"
                    }`}
                    alt="Article thumbnail"
                  />
                </div>
                <div className="article-content">
                  <h3>
                    {isPositive
                      ? "Setting Healthy Relationship Goals Together"
                      : "Moving Forward: Finding Joy After A Relationship Ends"}
                  </h3>
                  <p>
                    {isPositive
                      ? "How to create shared visions that strengthen your bond and future together."
                      : "Perspectives on healing and rediscovering happiness in your next chapter."}
                  </p>
                  <Link
                    to={`/blog/${
                      isPositive ? "relationship-goals" : "moving-forward"
                    }`}
                    className="article-link">
                    Read Article
                    <i className="fas fa-arrow-right"></i>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="result-footer">
          <div className="result-footer-content">
            <p className="result-disclaimer">
              This assessment is designed to provide general insights based on
              your responses and should not replace professional advice. If
              you're experiencing relationship distress or safety concerns,
              please consult with a qualified therapist or counselor.
            </p>

            <div className="result-footer-buttons">
              <Link to="/" className="footer-link">
                <i className="fas fa-home"></i>
                <span>Home</span>
              </Link>

              <Link to="/resources" className="footer-link">
                <i className="fas fa-book"></i>
                <span>Resources</span>
              </Link>

              <Link to="/contact" className="footer-link">
                <i className="fas fa-envelope"></i>
                <span>Contact</span>
              </Link>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Result;
